import { useMemo } from "react";

import { Edition, ProductKey } from "@/components/signup/utils";
import { defaultProductData } from "@/templates/try/cloud/signup/signup-page/src/utils";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { defaultProduct, Product, products } from "./products";

export const useProductIntent = (location: string) =>
    useMemo(() => getProductIntent(location), [location]);

// eslint-disable-next-line complexity
export const getProductIntent = (location: string) => {
    let edition: Edition | undefined;
    let product: Product | undefined;
    let bundle: string | undefined;
    let url: URL;

    try {
        url = new URL(location);
    } catch (e) {
        return { edition, product };
    }

    if (url.pathname.includes("/software/jira/product-discovery")) {
        product = {
            bundle: "jira-product-discovery",
            description: "Jira Product Discovery",
            key: ProductKey.JIRA_PRODUCT_DISCOVERY,
            name: "jira-product-discovery"
        };
        edition = "free";
    } else if (url.pathname.includes("/software/jira/service-management")) {
        if (
            checkFeatureFlagGate(
                featureFlags.signupServcoProductIntentOnJsmProductTour
            )
        ) {
            product = {
                bundle: "service-collection",
                description: "Service Collection",
                key: ProductKey.SERVICE_COLLECTION,
                name: "service-collection"
            };
        } else {
            product = {
                bundle: "jira-service-management",
                description: "Jira Service Management",
                key: ProductKey.JIRA_SERVICE_MANAGEMENT,
                name: "jira-servicedesk"
            };
        }
        edition = "free";
    } else if (url.pathname.includes("/software/jira")) {
        product = {
            bundle: "jira-software",
            description: "Jira",
            key: ProductKey.JIRA_SOFTWARE,
            name: "jira"
        };
        edition = "free";
    }

    if (url.pathname.includes("/software/confluence")) {
        product = {
            bundle: "confluence",
            description: "Confluence",
            key: ProductKey.CONFLUENCE,
            name: "confluence"
        };
        edition = "free";
    }

    const isSignupPage = url.pathname.endsWith("/try/cloud/signup");
    const isConfirmationPage = url.pathname.endsWith(
        "/ondemand/signup/confirmation"
    );

    if (isSignupPage || isConfirmationPage || window.IS_STORYBOOK) {
        // defaults for the signup page
        if (isSignupPage) {
            edition = "free";
            product = defaultProduct;
            bundle = defaultProduct.bundle;
        }

        // determine product
        if (url.searchParams.has("product")) {
            product = products.find(
                (product) => product.key === url.searchParams.get("product")
            );
            if (product?.bundle) {
                bundle = product.bundle;
            }
        } else if (url.searchParams.has("bundle")) {
            const bundleParam = url.searchParams.get("bundle") as string;

            // validate bundle
            const collections = ["service-collection", "teamwork-collection"];
            const isValidBundle =
                defaultProductData[bundleParam] !== undefined ||
                collections.includes(bundleParam);

            // only set bundle = param if valid param; this affects how we define product
            if (isValidBundle) {
                bundle = bundleParam;
            }

            product = products.find((product) => product.bundle === bundle);
        } else if (url.searchParams.has("products")) {
            product = products.find(
                (product) =>
                    product.key ===
                    url.searchParams.get("products")?.split(",")[0]
            );
        }

        // bxpkit developer signup bundle
        if (url.searchParams.get("developer")) {
            product = undefined;
            bundle = "developer";
        }

        // opsgenie EOL
        if (
            bundle == "opsgenie" &&
            checkFeatureFlagGate(featureFlags.gwp6524OpsgenieSignupEol)
        ) {
            bundle = "jira-service-management";
        }

        // determine edition
        const editionParam = url.searchParams.get("edition");
        if (editionParam === "free") {
            edition = "free";
        } else if (editionParam === "standard") {
            edition = "standard";
        } else if (editionParam === "premium") {
            edition = "premium";
        }

        /**
         * TODO: https://softwareteams.atlassian.net/browse/JOLT-551
         * Temporary handler for rovo dev standard while rovo
         * dev beta experience remains frozen till Jan 2026
         * more info at https://hello.atlassian.net/wiki/spaces/AIDO/pages/5744411836/Rovo+Dev+Customer+Cohort+Rollout+Plan
         */
        const rovoDevStandardEnabled = checkFeatureFlagGate(
            featureFlags.enableRovoDevStandardSignup
        );
        if (
            bundle === "devai" &&
            edition === "standard" &&
            rovoDevStandardEnabled
        ) {
            product = {
                ...product,
                description: "Rovo Dev"
            } as Product & { description: "Rovo Dev" };
        }
    }
    return { edition, product, bundle };
};
